// ----------------------------------------------------------------------------
// usePlanDraft — the Roster page's per-plan editing state.
//
// This state used to live loose in Roster.tsx as four independent useState
// calls, and the effect that reloaded it on a plan switch had two branches.
// One of them (restore a cached session draft) refreshed team and notes but
// not items, so the previously selected plan's setlist stayed in state and
// was published onto the new plan. RCCG hit this on 2026-10-25: the Oct 25
// setlist went out on all five November services.
//
// The shape here is the fix. The snapshot is built in one place and always
// carries every field, and the state is stamped with the plan it belongs to
// so nothing can be written back to a different plan.
// ----------------------------------------------------------------------------

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ServicePlan, TeamMemberAssignment, PlanItem } from '@app/types';

// Session-storage helpers for draft persistence across refresh
const DRAFT_KEY_PREFIX = 'roster-draft:';
const getDraftKey = (planId: string) => `${DRAFT_KEY_PREFIX}${planId}`;

export interface CachedDraft {
  team: TeamMemberAssignment[];
  notes: string;
}

export function saveDraftToSession(planId: string, team: TeamMemberAssignment[], notes: string) {
  try {
    sessionStorage.setItem(getDraftKey(planId), JSON.stringify({ team, notes }));
  } catch { /* quota exceeded — ignore */ }
}

export function loadDraftFromSession(planId: string): CachedDraft | null {
  try {
    const raw = sessionStorage.getItem(getDraftKey(planId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearDraftFromSession(planId: string) {
  sessionStorage.removeItem(getDraftKey(planId));
}

export interface PlanLocalState {
  team: TeamMemberAssignment[];
  notes: string;
  items: PlanItem[];
  hasUnsavedChanges: boolean;
}

/**
 * The editing state for `plan`, given whatever session draft exists for it.
 *
 * Returns a COMPLETE snapshot on every path. A caller that spreads this into
 * state cannot leave one field holding the previous plan's data, which is the
 * class of bug this replaces.
 *
 * `items` always comes from the server plan: the session draft stores team and
 * notes only, so it can never be the source of a setlist.
 */
export function nextLocalState(plan: ServicePlan, cached: CachedDraft | null): PlanLocalState {
  const items = plan.items || [];

  if (cached) {
    return { team: cached.team, notes: cached.notes, items, hasUnsavedChanges: true };
  }

  const seen = new Set<string>();
  const deduped = (plan.team || []).filter(m => {
    if (seen.has(m.userId)) return false;
    seen.add(m.userId);
    return true;
  });
  return { team: deduped, notes: plan.notes || '', items, hasUnsavedChanges: false };
}

/** Re-index a setlist for the wire, or undefined when there is nothing to send. */
export function orderedItems(items: PlanItem[]): PlanItem[] | undefined {
  return items.length > 0 ? items.map((item, idx) => ({ ...item, orderIndex: idx })) : undefined;
}

export function usePlanDraft(selectedPlanId: string | null, allPlans: ServicePlan[]) {
  const [selectedPlan, setSelectedPlan] = useState<ServicePlan | null>(null);
  const [localTeam, setLocalTeam] = useState<TeamMemberAssignment[]>([]);
  const [localNotes, setLocalNotes] = useState('');
  const [localItems, setLocalItems] = useState<PlanItem[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // The plan the local editing state above belongs to. Every path that fills
  // that state stamps this, and nothing may be saved onto a plan it does not
  // name — see itemsForSave.
  const loadedPlanIdRef = useRef<string | null>(null);

  useEffect(() => {
    const plan = selectedPlanId ? allPlans.find(p => p.id === selectedPlanId) ?? null : null;

    if (!plan) {
      setSelectedPlan(null);
      setLocalTeam([]);
      setLocalNotes('');
      setLocalItems([]);
      setHasUnsavedChanges(false);
      loadedPlanIdRef.current = null;
      return;
    }

    setSelectedPlan(plan);
    if (loadedPlanIdRef.current === plan.id) return; // already editing this plan

    const next = nextLocalState(plan, loadDraftFromSession(plan.id));
    setLocalTeam(next.team);
    setLocalNotes(next.notes);
    setLocalItems(next.items);
    setHasUnsavedChanges(next.hasUnsavedChanges);
    loadedPlanIdRef.current = plan.id;
  }, [selectedPlanId, allPlans]);

  // Persist unsaved changes to sessionStorage so they survive refresh.
  useEffect(() => {
    if (!selectedPlanId) return;
    // Only once the load effect has adopted this plan. Without the guard, a
    // plan switch persists the OUTGOING plan's team/notes under the INCOMING
    // plan's id, which comes back later as a bogus "cached draft".
    if (loadedPlanIdRef.current !== selectedPlanId) return;

    if (hasUnsavedChanges) saveDraftToSession(selectedPlanId, localTeam, localNotes);
    else clearDraftFromSession(selectedPlanId);
  }, [selectedPlanId, hasUnsavedChanges, localTeam, localNotes]);

  /**
   * The setlist to send when saving or publishing `plan`.
   *
   * Last line of defence: if the local state belongs to some other plan, the
   * server copy wins. A setlist is never carried across plans, whatever the
   * caller believes is selected.
   */
  const itemsForSave = useCallback((plan: ServicePlan): PlanItem[] | undefined => {
    if (loadedPlanIdRef.current !== plan.id) {
      if (import.meta.env?.DEV) {
        console.error(
          `[roster] setlist state belongs to plan ${loadedPlanIdRef.current}, ` +
          `refusing to save it onto ${plan.id}`
        );
      }
      return orderedItems(plan.items || []);
    }
    return orderedItems(localItems);
  }, [localItems]);

  /** Forget the session draft for a plan and force a reload from the server copy. */
  const clearDraft = useCallback((planId: string) => {
    clearDraftFromSession(planId);
    setHasUnsavedChanges(false);
    loadedPlanIdRef.current = null;
  }, []);

  /**
   * Throw away unsaved changes to `plan`: drop the session draft and put the
   * team, notes and setlist back to the saved copy straight away.
   */
  const discardDraft = useCallback((plan: ServicePlan) => {
    clearDraftFromSession(plan.id);
    const next = nextLocalState(plan, null);
    setLocalTeam(next.team);
    setLocalNotes(next.notes);
    setLocalItems(next.items);
    setHasUnsavedChanges(false);
    loadedPlanIdRef.current = plan.id;
  }, []);

  return {
    selectedPlan, setSelectedPlan,
    localTeam, setLocalTeam,
    localNotes, setLocalNotes,
    localItems, setLocalItems,
    hasUnsavedChanges, setHasUnsavedChanges,
    itemsForSave, clearDraft, discardDraft,
    loadedPlanIdRef,
  };
}
